import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import api, { advancedAmountAPI } from '../../services/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Payments = () => {
  const { currentUser } = useContext(AuthContext);
  const { formatCurrency, settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [villa, setVilla] = useState(null);
  const [maintenanceFee, setMaintenanceFee] = useState(null);
  const [societyInfo, setSocietyInfo] = useState(null);
  const [generatingPdfs, setGeneratingPdfs] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [form, setForm] = useState({
    unit_id: '',
    category: '',
    month_id: '',
    screenshot: null
  });

  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  
  // advance amounts state
  const [advancedAmounts, setAdvancedAmounts] = useState([]);
  const [advancedAmountsLoading, setAdvancedAmountsLoading] = useState(false);
  const [advancedAmountsError, setAdvancedAmountsError] = useState(null);

  // Payment categories and month options
  const paymentCategories = [
    { value: 'maintenance', label: 'Maintenance Fee' },
    { value: 'penalty', label: 'Penalty Fee' },
    { value: 'booking', label: 'Common Area Booking' },
    { value: 'other', label: 'Other' },
  ];

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Pagination state and logic
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = payments?.payments?.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(payments?.payments?.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('payments-table-section');
    }, 50);
  };

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('payments-table-section');
      }, 50);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('payments-table-section');
      }, 50);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [payments?.payments]);
  
  // Generate a unique CSS class name based on the current settings
  const themeClassName = `theme-header-${Date.now()}`;
  // Get theme colors from settings
  const bgColor = settings.block_title_bg_color || '#f8f9fa';
  const textColor = settings.block_title_color || '#8B4513';

  // Add CSS styles directly to the document head
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      /* Apply direct styles to these elements */
      .card-header.${themeClassName},
      .modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
        border-color: ${bgColor} !important;
      }
      /* Force all children to have the text color */
      .card-header.${themeClassName} *,
      .modal-header.${themeClassName} * {
        color: ${textColor} !important;
      }
      /* Override any other rules */
      .card .card-header.${themeClassName},
      .modal .modal-header.${themeClassName},
      .card-header.${themeClassName},
      .modal-header.${themeClassName},
      div.card-header.${themeClassName},
      div.modal-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      /* Direct style application */
      #root .card-header.${themeClassName},
      html body .card-header.${themeClassName} {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
      /* For Description section specifically */
      .card-header.${themeClassName}[class*="theme-header"] {
        background-color: ${bgColor} !important;
        color: ${textColor} !important;
      }
    `;
    document.head.appendChild(styleTag);
    return () => {
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, [themeClassName, bgColor, textColor]);

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'screenshot' && files && files[0]) {
      const file = files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setFormError('Only JPG, JPEG, and PNG images are allowed.');
        setForm(f => ({ ...f, screenshot: null }));
        setImagePreview(null);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setFormError('Image size must be less than 10MB.');
        setForm(f => ({ ...f, screenshot: null }));
        setImagePreview(null);
        return;
      }
      setFormError(null);
      setForm(f => ({ ...f, screenshot: file }));
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      setForm(f => ({ ...f, [name]: trimmedValue }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);
    const trimmedForm = {
      unit_id: form.unit_id?.toString().trim(),
      category: form.category?.toString().trim(),
      month_id: form.month_id?.toString().trim()
    };
    if (!trimmedForm.unit_id || trimmedForm.unit_id === '') {
      setFormError('Please select a unit.');
      setFormLoading(false);
      return;
    }
    if (!trimmedForm.category || trimmedForm.category === '') {
      setFormError('Please select a payment category.');
      setFormLoading(false);
      return;
    }
    if (!trimmedForm.month_id || trimmedForm.month_id === '') {
      setFormError('Please select a month.');
      setFormLoading(false);
      return;
    }
    if (!form.screenshot) {
      setFormError('Please upload a payment screenshot.');
      setFormLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('unit_id', trimmedForm.unit_id);
      formData.append('category', trimmedForm.category);
      formData.append('month_id', trimmedForm.month_id);
      formData.append('screenshot', form.screenshot);
      formData.append('user_id', currentUser?.id);
      formData.append('organization_id', currentUser?.organization_id);
      await api.post('/api/owner-payments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormSuccess('Payment submitted successfully!');
      setForm({ unit_id: villa ? villa.id : '', category: '', month_id: '', screenshot: null });
      // setForm({ unit_id: 1, category: '', month_id: '', screenshot: null });
      setImagePreview(null);
      const fileInput = document.querySelector('input[name="screenshot"]');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to submit payment.');
    } finally {
      setFormLoading(false);
    }
  };

  // Custom styles based on theme settings
  // const blockTitleStyle = {
  //   backgroundColor: settings.block_title_bg_color || '#f8f9fa',
  //   color: settings.block_title_color || '#8B4513'
  // };
  
  // Style for the payment summary card headers
  const summaryHeaderStyle = {
    backgroundColor: settings.theme_primary_color || '#0d6efd', 
    color: '#ffffff',
    padding: '0.5rem 1rem',
    borderRadius: '0.25rem 0.25rem 0 0',
    fontWeight: '500'
  };

  // Fetch society settings first, as top priority
  useEffect(() => {
    const fetchMaintenanceFee = async () => {
      try {
        // Try society settings for maintenance fee
        const settingsEndpoint = '/api/settings/public'; 
        const response = await api.get(settingsEndpoint);
        setSocietyInfo(response.data); // Store society info for receipt
        
        if (response.data && response.data.maintenance_fee !== undefined) 
        {
          const fee = parseFloat(response.data.maintenance_fee);
          if (!isNaN(fee) && fee > 0) 
          { 
            setMaintenanceFee(fee);
            return; // Exit if found valid maintenance fee in settings
          }
        } 
      } catch (err) {
        console.error("Error fetching maintenance fee:", err); 
      }
    };
    
    fetchMaintenanceFee();
  }, [currentUser]);

  // Fetch payments data and villa info
  useEffect(() => {
    const fetchPaymentsAndVilla = async () => {
      setLoading(true);
      try { 
        // Import financialAPI
        const { financialAPI } = require('../../services/api');
        
        // Fetch both payments and villa data
        const [paymentsResponse, villaResponse] = await Promise.all([
          financialAPI.getOwnerPayments(),
          api.get('/api/villas/owner')
        ]);

        if (Array.isArray(paymentsResponse.data?.payments)) {
          setPayments(paymentsResponse.data);
          setError(null);
        } else {
          setPayments([]);
        }
        
        // Set villa data
        if (Array.isArray(villaResponse.data) && villaResponse.data.length > 0) {
          const villaData = villaResponse.data[0];
          setVilla(villaData); // Use the first villa for the owner
          
          // Check if villa has a specific maintenance fee and use it
          if (villaData.maintenance_fee !== undefined && 
              villaData.maintenance_fee !== null && 
              parseFloat(villaData.maintenance_fee) > 0) {
            const villaFee = parseFloat(villaData.maintenance_fee);
            if (!isNaN(villaFee)) {
              setMaintenanceFee(villaFee);
            }
          }
        } else {
          setVilla(null);
        }
        
      } catch (err) {
        console.error('Error in fetchPaymentsAndVilla:', err);
        setPayments([]);
        setVilla(null);
        setError('Failed to load payment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentsAndVilla();
  }, [currentUser]);

  // Fetch advance amounts for the current user's unit
  useEffect(() => {
    const fetchAdvancedAmounts = async () => {
      if (!villa || !villa.id) return;
      
      try {
        setAdvancedAmountsLoading(true);
        setAdvancedAmountsError(null);
        
        const response = await advancedAmountAPI.getByUnitId(villa.id);
        
        if (response.data && response.data.success) {
          setAdvancedAmounts(response.data.data || []);
        } else {
          setAdvancedAmountsError('Failed to fetch advance amounts');
        }
      } catch (err) {
        setAdvancedAmountsError('Unable to fetch advance amounts. Please try again later.');
        console.error('Error fetching advance amounts:', err);
      } finally {
        setAdvancedAmountsLoading(false);
      }
    };
    
    fetchAdvancedAmounts();
  }, [villa]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  // Helper function to format payment method for display
  const formatPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) return { text: 'Not specified', class: 'bg-secondary' };
    
    const methodConfig = {
      'cash_payment': { text: 'Cash', class: 'bg-success' },
      'from_advance_balance': { text: 'From advance balance', class: 'bg-info' },
      'cash_from_advance_balance': { text: 'Cash + from advance balance', class: 'bg-warning text-dark' }
    };
    
    return methodConfig[paymentMethod] || { text: paymentMethod, class: 'bg-secondary' };
  };

  // Generate PDF receipt
  const generateReceiptPDF = (payment) => {
    try {
      // Initialize the PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Define text styles
      const titleFontSize = 20;
      const normalFontSize = 10;
      const smallFontSize = 8;
      
      // Society information
      const societyName = societyInfo?.society_name || settings?.society_name || 'Hitesh Homes';
      const societyAddress = societyInfo?.society_address || settings?.society_address || '123 Society Address, City, State';
      const societyContact = societyInfo?.contact_phone || settings?.contact_phone || 'Phone: +91 99999 99999';
      const societyEmail = societyInfo?.contact_email || settings?.contact_email || 'Email: contact@hiteshhomes.com';
      
      // Try to add society logo if available
      if (settings.logo_url) {
        try {
          const logoX = 15;
          const logoY = 15;
          const logoWidth = 25;
          const logoHeight = 25;
          doc.addImage(settings.logo_url, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        } catch (error) {
          console.error('Error adding logo to PDF:', error);
          // Continue without logo if there's an error
        }
      }
      
      // Logo and header
      doc.setFontSize(titleFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text(societyName, pageWidth / 2, 20, { align: 'center' });
      
      // Society address and contact
      doc.setFontSize(smallFontSize);
      doc.setFont('helvetica', 'normal');
      doc.text(societyAddress, pageWidth / 2, 25, { align: 'center' });
      doc.text(societyContact, pageWidth / 2, 30, { align: 'center' });
      doc.text(societyEmail, pageWidth / 2, 35, { align: 'center' });
      
      // Add decorative line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(15, 40, pageWidth - 15, 40);
      
      // Receipt title with background
      doc.setFillColor(240, 240, 240);
      doc.rect(15, 45, pageWidth - 30, 10, 'F');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 100, 150);
      doc.text('PAYMENT RECEIPT', pageWidth / 2, 52, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      
      // Receipt number and date
      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text(`Receipt Number: ${payment.receipt_number || '-'}`, 15, 60);
      doc.text(`Date: ${formatDate(payment.date)}`, pageWidth - 15, 60, { align: 'right' });
      
      // Add a box around owner information
      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(15, 65, pageWidth - 30, 25, 2, 2, 'FD');
      
      // Owner information
      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'normal');
      doc.text('Received From:', 20, 72);
      doc.setFont('helvetica', 'bold');
      doc.text(currentUser?.name || 'Owner', 70, 72);
      
      doc.setFont('helvetica', 'normal');
      doc.text('Unit Details:', 20, 82);
      doc.setFont('helvetica', 'bold');
      doc.text(`${villa?.unit_type || 'Unit'} ${villa?.villa_number || ''}`, 70, 82);
      
      // Payment details
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      
      // Style the payment details header
      doc.setFillColor(51, 122, 183);
      doc.rect(15, 95, pageWidth - 30, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text('PAYMENT DETAILS', pageWidth / 2, 101, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      
      // Draw payment details manually instead of using autoTable
      const tableStartY = 105;
      const tableWidth = pageWidth - 30;
      const descColumnWidth = tableWidth * 0.75; // 75% for description
      const amountColumnWidth = tableWidth * 0.25; // 25% for amount
      
      // Build description with remarks FIRST to calculate height
      const categoryText = payment.category || payment.type || 'Payment';
      let descriptionText = categoryText;
      
      // Add remarks if available
      if (payment.remarks) {
        descriptionText = `${categoryText}, Remarks: ${payment.remarks}`;
      }
      
      // Calculate text wrapping and height needed
      const maxDescWidth = descColumnWidth - 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const descriptionLines = doc.splitTextToSize(descriptionText, maxDescWidth);
      const lineHeight = 5;
      const contentHeight = Math.max(10, descriptionLines.length * lineHeight + 4); // Minimum 10, or calculated height
      const totalTableHeight = 10 + contentHeight; // Header (10) + Content
      
      // Now draw the table with correct height
      // Table header background
      doc.setFillColor(220, 220, 220);
      doc.rect(15, tableStartY, tableWidth, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Description', 15 + 5, tableStartY + 7);
      doc.text('Amount', 15 + descColumnWidth + 5, tableStartY + 7);
      
      // Table border (with calculated height)
      doc.setDrawColor(0, 0, 0);
      doc.rect(15, tableStartY, tableWidth, totalTableHeight, 'S');
      
      // Vertical divider (full height)
      doc.line(15 + descColumnWidth, tableStartY, 15 + descColumnWidth, tableStartY + totalTableHeight);
      
      // Horizontal divider (separating header from content)
      doc.line(15, tableStartY + 10, 15 + tableWidth, tableStartY + 10);
      
      // Draw description text (may be multiline)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      descriptionLines.forEach((line, index) => {
        doc.text(line, 15 + 5, tableStartY + 16 + (index * lineHeight));
      });
      
      // Draw amount
      doc.text(formatCurrency(payment.amount || 0).replace('₹', 'Rs.'), 15 + descColumnWidth + 5, tableStartY + 16);
      
      // Calculate extra height for positioning elements below
      const extraHeight = contentHeight - 10;
      
      // Calculate final position after table
      const finalY = tableStartY + 30 + extraHeight;
      
      // Footer with total amount in words
      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'normal');
      
      // Add a box around amount in words
      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(15, finalY - 5, pageWidth - 30, 20, 2, 2, 'FD');
      
      doc.text('Amount in words:', 20, finalY + 5);
      doc.setFont('helvetica', 'bold');
      
      // Convert amount to words (simplified)
      const amountInWords = `${numberToWords(payment.amount || 0)} rupees only`;
      doc.text(amountInWords, 20, finalY + 12);
      
      // Payment status
      doc.setFontSize(14);
      doc.setFillColor(39, 174, 96);
      doc.rect(pageWidth - 60, finalY + 25, 45, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text('PAID', pageWidth - 38, finalY + 32, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      
      // Authorized signatory
      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'normal');
      doc.text('For ' + societyName, pageWidth - 15, finalY + 50, { align: 'right' });
      doc.line(pageWidth - 60, finalY + 45, pageWidth - 15, finalY + 45);
      doc.text('Authorized Signatory', pageWidth - 15, finalY + 55, { align: 'right' });
      
      // Add border around the page
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.rect(10, 10, pageWidth - 20, 275);
      
      // Add footer with terms
      const footerY1 = doc.internal.pageSize.getHeight() - 15 - 8; // 8 = font height estimate
      const footerY2 = footerY1 + 5;

      doc.setFontSize(8);
      doc.text('This is a computer-generated receipt and does not require physical signature.', pageWidth / 2, footerY1, { align: 'center' });
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, footerY2, { align: 'center' });
      
      // Download PDF
      doc.save(`Receipt-${payment.receipt_number || 'Payment'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF receipt. Please try again later.');
    }
  };
  
  // Helper function to convert number to words
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numStr = num.toString();
    
    if (num < 20) return ones[num];
    
    if (numStr.length === 2) {
      return tens[parseInt(numStr[0])] + (numStr[1] !== '0' ? ' ' + ones[parseInt(numStr[1])] : '');
    }
    
    if (numStr.length === 3) {
      return ones[parseInt(numStr[0])] + ' Hundred ' + (parseInt(numStr.substring(1)) !== 0 ? numberToWords(parseInt(numStr.substring(1))) : '');
    }
    
    if (numStr.length <= 5) {
      const thousands = parseInt(numStr.substring(0, numStr.length - 3));
      const remainder = parseInt(numStr.substring(numStr.length - 3));
      return numberToWords(thousands) + ' Thousand ' + (remainder !== 0 ? numberToWords(remainder) : '');
    }
    
    return num.toString(); // Fallback for larger numbers
  };

  // Generate all receipts
  const generateAllReceipts = async () => {
    try {
      if (payments?.payments?.length === 0) return;
      
      setGeneratingPdfs(true);
      
      const paidPayments = payments?.payments?.filter(payment => payment.status === 'paid');
      for (let i = 0; i < paidPayments.length; i++) {
        const payment = paidPayments[i];
        // Generate after a small delay to prevent browser freezing
        await new Promise(resolve => {
          setTimeout(() => {
            generateReceiptPDF(payment);
            resolve();
          }, 500);
        });
      }
      
      setGeneratingPdfs(false);
    } catch (error) {
      console.error('Error generating all receipts:', error);
      setGeneratingPdfs(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mt-4">
      <PageTitle 
        icon="fa-credit-card" 
        title="My Payments" 
        className="mb-4"
      />
      
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {villa ? (
        <div className="card mb-4 shadow-sm">
          <div className="card-header"
          style={{
              backgroundColor: bgColor,
              color: textColor
            }}
          >
            <h5 className="mb-0">{villa.unit_type || 'Unit'} {villa.villa_number} Payment Summary</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Monthly Maintenance
                  </div>
                  <div className="card-body text-center">
                    <h4>{formatCurrency(maintenanceFee)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Last Payment
                  </div>
                  <div className="card-body text-center">
                    <h4>{payments?.last_payment_date ? payments?.last_payment_date : 'No payments'}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Total Paid
                  </div>
                  <div className="card-body text-center">
                    <h4>{formatCurrency(payments?.total_paid)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Additional Charges
                  </div>
                  <div className="card-body text-center">
                    <h4>{formatCurrency(settings.additional_charges || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Advance Amount
                  </div>
                  <div className="card-body text-center">
                    <h4>
                      {formatCurrency(payments?.advance_paid)}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-3">
                  <div style={summaryHeaderStyle} className="text-center">
                    Total Pendings
                  </div>
                  <div className="card-body text-center">
                    <h4>{formatCurrency(payments?.total_pendings)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-warning mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No property information found. Please contact management if this is an error.
        </div>
      )}

      <div className="card shadow-sm" id="payments-table-section">
        <div className="card-header"
        style={{
            backgroundColor: bgColor,
            color: textColor
          }}
        >
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <h5 className="mb-0">Payment History</h5>
            {payments && payments?.payments?.filter(p => p.status === 'paid')?.length > 0 && (
              <button 
                className="btn btn-outline-primary btn-sm searchFilterButton"
                onClick={generateAllReceipts}
                disabled={generatingPdfs}
              >
                {generatingPdfs ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-download me-1"></i>
                    Download All Receipts
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {payments?.payments?.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Payment For</th>
                    <th>Amount</th>
                    <th>Remaining Amount</th>
                    <th>Type</th>
                    <th>Receipt Number</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((payment) => (
                    <tr key={payment.id || Math.random().toString()}>
                      <td>{formatDate(payment.date)}</td>
                      <td>
                        {payment.month_id ? (
                          <span className="badge bg-primary">{monthNames[payment.month_id - 1]}{payment.year ? `, ${payment.year}` : ""}</span>
                        ) : '-'}
                      </td>
                      <td>{formatCurrency(payment.amount || 0)}</td>
                      <td>{formatCurrency(payment.remaining_amount || 0)}</td>
                      <td>{payment.type || payment.category || 'Payment'}</td>
                      <td>{payment.receipt_number || '-'}</td>
                      <td>
                        {payment.payment_method ? (
                          <span className={`badge ${formatPaymentMethod(payment.payment_method).class}`}>
                            {formatPaymentMethod(payment.payment_method).text}
                          </span>
                        ) : (
                          <span className="badge bg-secondary">Not Specified</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${payment.status === 'paid' ? 'bg-success' : payment.status === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                          {payment.status 
                            ? payment.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) 
                            : 'Pending'}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className='btn-group btn-group-sm' role='group'>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => { setSelectedPayment(payment); setShowModal(true); }}
                            title="View Details"
                          >
                            <i className="fas fa-eye me-1"></i> View
                          </button>
                          {payment.status === 'paid' && (
                            <button 
                              className="btn btn-sm btn-primary" 
                              onClick={() => generateReceiptPDF(payment)}
                              title="Download Receipt"
                            >
                              <i className="fas fa-download me-1"></i> Receipt
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, payments?.payments.length)} of {payments?.payments.length} payments
                  </div>
                  <nav aria-label="Payments pagination">
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                          <i className="fas fa-chevron-left"></i>
                        </button>
                      </li>
                      {Array.from({ length: totalPages }).map((_, index) => {
                        const pageNumber = index + 1;
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => paginate(pageNumber)}>
                                {pageNumber}
                              </button>
                            </li>
                          );
                        }
                        if (
                          (pageNumber === 2 && currentPage > 3) ||
                          (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                        ) {
                          return (
                            <li key={pageNumber} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No payment records found.
            </div>
          )}
        </div>
      </div>
      
      {/* advance amounts Section */}
      <div className="card shadow-sm mt-4">
        <div className="card-header" style={{
            backgroundColor: bgColor,
            color: textColor
          }}>
          <h5 className="mb-0">Advance Amounts</h5>
        </div>
        <div className="card-body">
          {advancedAmountsError && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle me-2"></i>
              {advancedAmountsError}
            </div>
          )}
          
          {advancedAmountsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading advance amounts...</p>
            </div>
          ) : advancedAmounts.length > 0 ? (
            <>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {advancedAmounts.map((amount) => (
                    <tr key={amount.id}>
                      <td>{formatDate(amount.date)}</td>
                      <td>
                        <span className={`badge ${amount.advance_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                          {amount.advance_type === 'credit' ? 'Credit' : 'Debit'}
                        </span>
                      </td>
                      <td className={amount.advance_type === 'credit' ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        {amount.advance_type === 'credit' ? '+' : '-'}{formatCurrency(amount.amount || 0)}
                      </td>
                      <td>
                        <span className={`badge ${amount.status === 'active' ? 'bg-success' : amount.status === 'used' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                          {amount.status ? amount.status.charAt(0).toUpperCase() + amount.status.slice(1) : 'Active'}
                        </span>
                      </td>
                      <td>{amount.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card bg-light">
                    <div className="card-body text-center">
                      <h6 className="card-title text-success py-2"><strong>Total Credits</strong></h6>
                      <h5 className="text-success"><strong>
                        {formatCurrency(
                          advancedAmounts
                            .filter(a => a.advance_type === 'credit')
                            .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0)
                        )}</strong>
                      </h5>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-light">
                    <div className="card-body text-center">
                      <h6 className="card-title text-danger py-2"><strong>Total Debits</strong></h6>
                      <h5 className="text-danger"><strong>
                        {formatCurrency(
                          advancedAmounts
                            .filter(a => a.advance_type === 'debit')
                            .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0)
                        )}</strong>
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="row mt-2">
                <div className="col-12">
                  <div className="card bg-info text-white">
                    <div className="card-body text-center">
                      <h6 className="card-title py-2"><strong>Net Balance</strong></h6>
                      <h4 className="text-secondary"><strong>
                        {formatCurrency(
                          advancedAmounts
                            .filter(a => a.advance_type === 'credit')
                            .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0) -
                          advancedAmounts
                            .filter(a => a.advance_type === 'debit')
                            .reduce((sum, a) => sum + parseFloat(a.amount || 0), 0)
                        )}</strong>
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No advance amounts found for your unit.
            </div>
          )}
        </div>
      </div>
      
      <div className="card shadow-sm mt-4">
        <div className="card-header" style={{
            backgroundColor: bgColor,
            color: textColor
          }}>
          <h5 className="mb-0">Submit New Payment</h5>
        </div>

        <div className="card-body">
          {formError && <div className="alert alert-danger">{formError}</div>}
          {formSuccess && <div className="alert alert-success">{formSuccess}</div>}
          <form onSubmit={handleFormSubmit} encType="multipart/form-data">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Unit</label>
                <select
                  className="form-select"
                  name="unit_id"
                  value={form.unit_id}
                  onChange={handleFormChange}
                >
                  <option value="">Select Unit</option>
                  {villa && (
                    <option key={villa.id} value={villa.id}>
                      {villa.unit_type ? villa.unit_type + ' ' : ''}{villa.villa_number}
                    </option>
                  )}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                >
                  <option value="">Select Category</option>
                  {paymentCategories.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Month</label>
                <select
                  className="form-select"
                  name="month_id"
                  value={form.month_id}
                  onChange={handleFormChange}
                >
                  <option value="">Select Month</option>
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Payment Screenshot</label>
                <input
                  type="file"
                  className="form-control"
                  name="screenshot"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleFormChange}
                />

                {imagePreview && (
                  <div className="mt-2 position-relative" style={{ maxWidth: 180 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="img-thumbnail"
                      style={{ maxHeight: 120, maxWidth: 180 }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="text-end mt-3">
              <button type="submit" className="btn btn-primary" disabled={formLoading}>
                {formLoading ? 'Submitting...' : 'Submit Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showModal && selectedPayment && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{
              backgroundColor: bgColor,
              color: textColor
            }}>Payment Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}
                style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Date:</strong> {formatDate(selectedPayment.date)}</p>
                    <p><strong>Amount:</strong> {formatCurrency(selectedPayment.amount || 0)}</p>
                    <p><strong>Type:</strong> 
                    {(selectedPayment.type || selectedPayment.category || 'Payment').replace(/_/g, ' ')
                          .replace(/\b\w/g, c => c.toUpperCase())}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Receipt Number:</strong> {selectedPayment.receipt_number || '-'}</p>
                    <p><strong>Payment Method:</strong> 
                      {selectedPayment.payment_method ? (
                        <span className={`badge ${formatPaymentMethod(selectedPayment.payment_method).class} ms-2`}>
                          {formatPaymentMethod(selectedPayment.payment_method).text}
                        </span>
                      ) : (
                        <span className="badge bg-secondary ms-2">Not Specified</span>
                      )}
                    </p>
                    <p><strong>Status:</strong> 
                      <span className={`badge ${selectedPayment.status === 'paid' ? 'bg-success' : selectedPayment.status === 'pending' ? 'bg-warning' : 'bg-secondary'} ms-2`}>
                        {selectedPayment.status ? selectedPayment.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Pending'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* advance amount Usage Section */}
                {selectedPayment.use_advanced_amount > 0 && (
                  <div className="mt-4">
                    <h6 className="border-bottom pb-2">Advance Amount Usage</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <small className="text-muted">Advance Amount Used</small>
                        <div className="fw-bold text-info">
                          {formatCurrency(selectedPayment.advanced_amount_used || 0)}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted">Cash Payment</small>
                        <div className="fw-bold text-success">
                          {formatCurrency(selectedPayment.cash_payment || 0)}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <small className="text-muted">Total Amount</small>
                        <div className="fw-bold text-primary">
                          {formatCurrency(selectedPayment.amount || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Visual Payment Breakdown */}
                {selectedPayment.payment_method && (selectedPayment.payment_method === 'cash_from_advance_balance' || selectedPayment.payment_method === 'from_advance_balance') && (
                  <div className="mt-4">
                    <h6 className="border-bottom pb-2">Payment Breakdown</h6>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="card border-primary">
                          <div className="card-body text-center p-3">
                            <h6 className="card-title mb-1 py-2"><strong>Total Amount</strong></h6>
                            <h5 className="text-primary mb-0">
                              {formatCurrency(selectedPayment.amount || 0)}
                            </h5>
                          </div>
                        </div>
                      </div>
                      
                      {selectedPayment.advanced_amount_used && parseFloat(selectedPayment.advanced_amount_used) > 0 && (
                        <div className="col-md-4">
                          <div className="card border-info">
                            <div className="card-body text-center p-3">
                              <h6 className="card-title mb-1 py-2"><strong>Advance Amount</strong></h6>
                              <h5 className="text-info mb-0">
                                {formatCurrency(selectedPayment.advanced_amount_used || 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {selectedPayment.cash_payment && parseFloat(selectedPayment.cash_payment) > 0 && (
                        <div className="col-md-4">
                          <div className="card border-success">
                            <div className="card-body text-center p-3">
                              <h6 className="card-title mb-1 py-2"><strong>Cash Payment</strong></h6>
                              <h5 className="text-success mb-0">
                                {formatCurrency(selectedPayment.cash_payment || 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Information */}
                {selectedPayment.remarks && (
                  <div className="mt-4">
                    <h6 className="border-bottom pb-2">Remarks</h6>
                    <p className="text-muted">{selectedPayment.remarks}</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>

  );
};

export default Payments; 